import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Stack, Chip, Tab, Tabs, Checkbox, Toolbar, Autocomplete,
  Tooltip, alpha,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowForward as CarryIcon,
  Search as SearchIcon, SmartToy as AiIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { workItemsApi, categoriesApi, tagsApi } from '../api/services';
import type { WorkItem, WorkItemType, Priority, WorkItemStatus, Category, Tag } from '../types/models';
import PriorityChip from '../components/PriorityChip';
import StatusChip from '../components/StatusChip';

const workItemTypes: WorkItemType[] = ['Task', 'Decision', 'Briefing', 'Review', 'FollowUp', 'Draft', 'Reading'];
const priorities: Priority[] = ['Critical', 'High', 'Medium', 'Low'];
const statuses: WorkItemStatus[] = ['Backlog', 'Planned', 'InProgress', 'Done', 'Deferred', 'Cancelled'];

export default function WorkItemsPage() {
  const [items, setItems] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<WorkItem> | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Bulk selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');

  // Categories & tags for the create/edit dialog
  const [categories, setCategories] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [editTagNames, setEditTagNames] = useState<string[]>([]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await workItemsApi.getAll();
      setItems(data);
    } catch (err) {
      console.error('Failed to load work items', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // Load categories and tags once
  useEffect(() => {
    categoriesApi.getAll().then(setCategories).catch(() => {});
    tagsApi.getAll().then(setAllTags).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!editItem?.title) return;
    try {
      if (editItem.id) {
        await workItemsApi.update(editItem.id, editItem);
      } else {
        await workItemsApi.create(editItem);
      }
      setDialogOpen(false);
      setEditItem(null);
      loadItems();
    } catch (err) {
      console.error('Failed to save work item', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this work item?')) return;
    try {
      await workItemsApi.delete(id);
      loadItems();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleCarryForward = async (id: string) => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    try {
      await workItemsApi.carryForward(id, tomorrow);
      loadItems();
    } catch (err) {
      console.error('Failed to carry forward', err);
    }
  };

  // Bulk actions
  const handleBulkCarryForward = async () => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    try {
      await Promise.all(Array.from(selected).map(id => workItemsApi.carryForward(id, tomorrow)));
      setSelected(new Set());
      loadItems();
    } catch (err) {
      console.error('Bulk carry forward failed', err);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected work items?`)) return;
    try {
      await Promise.all(Array.from(selected).map(id => workItemsApi.delete(id)));
      setSelected(new Set());
      loadItems();
    } catch (err) {
      console.error('Bulk delete failed', err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids: string[]) => {
    const allSelected = ids.every(id => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ids));
    }
  };

  // Tab filter
  const tabFiltered = tabValue === 0
    ? items.filter(i => !['Done', 'Cancelled'].includes(i.status))
    : tabValue === 1
      ? items.filter(i => i.status === 'Done')
      : items;

  // Search & filter
  const filteredItems = useMemo(() => {
    let result = tabFiltered;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        i.title.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q)
      );
    }
    if (filterType) result = result.filter(i => i.type === filterType);
    if (filterPriority) result = result.filter(i => i.priority === filterPriority);
    return result;
  }, [tabFiltered, searchQuery, filterType, filterPriority]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Work Items</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
          setEditItem({
            title: '', type: 'Task', priority: 'Medium', status: 'Backlog',
            estimatedMinutes: 30, targetDate: format(new Date(), 'yyyy-MM-dd'),
            flags: [],
          });
          setEditTagNames([]);
          setDialogOpen(true);
        }}>
          Add Work Item
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); setSelected(new Set()); }} sx={{ mb: 2 }}>
        <Tab label={`Active (${items.filter(i => !['Done', 'Cancelled'].includes(i.status)).length})`} />
        <Tab label={`Completed (${items.filter(i => i.status === 'Done').length})`} />
        <Tab label={`All (${items.length})`} />
      </Tabs>

      {/* Search & filter bar */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search title or description..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 18, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filterType} label="Type" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {workItemTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={filterPriority} label="Priority" onChange={e => setFilterPriority(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        {(searchQuery || filterType || filterPriority) && (
          <Button size="small" onClick={() => { setSearchQuery(''); setFilterType(''); setFilterPriority(''); }}>
            Clear
          </Button>
        )}
      </Stack>

      {/* Bulk actions toolbar */}
      {selected.size > 0 && (
        <Toolbar
          sx={{
            mb: 1, borderRadius: 2, minHeight: 48,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            px: 2,
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
            {selected.size} selected
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<CarryIcon />} onClick={handleBulkCarryForward}>
              Carry Forward All
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDelete}>
              Delete Selected
            </Button>
          </Stack>
        </Toolbar>
      )}

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && selected.size < filteredItems.length}
                  checked={filteredItems.length > 0 && selected.size === filteredItems.length}
                  onChange={() => toggleSelectAll(filteredItems.map(i => i.id))}
                  size="small"
                />
              </TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Flags</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => (
              <TableRow key={item.id} hover selected={selected.has(item.id)}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{item.title}</Typography>
                  {item.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {item.description.substring(0, 80)}{item.description.length > 80 ? '...' : ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell><Chip label={item.type} size="small" variant="outlined" /></TableCell>
                <TableCell><PriorityChip priority={item.priority} /></TableCell>
                <TableCell><StatusChip status={item.status} /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{item.estimatedMinutes}m</span>
                    {item.aiEstimatedMinutes != null && item.aiEstimatedMinutes !== item.estimatedMinutes && (
                      <Tooltip title={`AI estimated: ${item.aiEstimatedMinutes}m`} arrow>
                        <Chip
                          icon={<AiIcon sx={{ fontSize: 14 }} />}
                          label={`${item.aiEstimatedMinutes}m`}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{item.targetDate}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {item.isCarryForward && <Chip label="Carry" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />}
                    {item.flags?.map(f => (
                      <Chip key={f} label={f} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => { setEditItem(item); setEditTagNames(item.workItemTags?.map(wt => wt.tag?.name || '').filter(Boolean) as string[] || []); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleCarryForward(item.id)} title="Carry forward"><CarryIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No work items</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem?.id ? 'Edit Work Item' : 'New Work Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth value={editItem?.title || ''} onChange={e => setEditItem(prev => ({ ...prev, title: e.target.value }))} />
            <TextField label="Description" fullWidth multiline rows={3} value={editItem?.description || ''} onChange={e => setEditItem(prev => ({ ...prev, description: e.target.value }))} />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={editItem?.type || 'Task'} label="Type" onChange={e => setEditItem(prev => ({ ...prev, type: e.target.value as WorkItemType }))}>
                  {workItemTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={editItem?.priority || 'Medium'} label="Priority" onChange={e => setEditItem(prev => ({ ...prev, priority: e.target.value as Priority }))}>
                  {priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={editItem?.status || 'Backlog'} label="Status" onChange={e => setEditItem(prev => ({ ...prev, status: e.target.value as WorkItemStatus }))}>
                  {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Duration (min)" type="number" fullWidth value={editItem?.estimatedMinutes || 30} onChange={e => setEditItem(prev => ({ ...prev, estimatedMinutes: parseInt(e.target.value) || 30 }))} />
            </Stack>
            <TextField label="Target Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={editItem?.targetDate || ''} onChange={e => setEditItem(prev => ({ ...prev, targetDate: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={editItem?.categoryId || ''}
                label="Category"
                onChange={e => {
                  const cat = categories.find(c => c.id === e.target.value);
                  setEditItem(prev => ({ ...prev, categoryId: e.target.value || null, category: cat?.name || '' }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              options={allTags.map(t => t.name)}
              value={editTagNames}
              onChange={(_, newVal) => setEditTagNames(newVal)}
              freeSolo
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tags..." />}
            />
            <TextField label="Notes" fullWidth multiline rows={2} value={editItem?.notes || ''} onChange={e => setEditItem(prev => ({ ...prev, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
